import type {
  ContentType,
  ObjectsRealEstate,
  ObjectsSegment,
  OrganizationCompany,
  OrganizationExternalClient,
} from '@pigello/construct-core';
import { api, fetchApiQuery, query } from '../api';
import type { GlobalFilter, GlobalFilterItem, GlobalFilterType } from './types';

export const FILTER_TYPE_LABEL: Record<GlobalFilterType, string> = {
  filter_external_clients: 'Uppdragsgivare',
  global_segments: 'Segment',
  global_realestates: 'Fastighet',
  global_companies: 'Bolag',
};

/** The kinds of thing the filter is scoping to, as a Swedish phrase —
 *  "Fastighet", "Uppdragsgivare & bolag", "Uppdragsgivare, bolag & fastighet".
 *  Empty string when nothing is filtered. */
export function filterScopeLabel(items: readonly GlobalFilterItem[]): string {
  const [first, ...rest] = [...new Set(items.map((item) => item.type))].map(
    (type) => FILTER_TYPE_LABEL[type]
  );
  if (!first) {
    return '';
  }
  if (rest.length === 0) {
    return first;
  }
  const lower = rest.map((label) => label.toLowerCase());
  const last = lower.pop();
  return `${[first, ...lower].join(', ')} & ${last}`;
}

type FilterModel =
  | ObjectsRealEstate
  | ObjectsSegment
  | OrganizationExternalClient
  | OrganizationCompany;

interface FilterTask {
  contentType: ContentType;
  ids: string[];
  type: GlobalFilterItem['type'];
}

export async function getGlobalFilterItems(
  filter: GlobalFilter[string]
): Promise<GlobalFilterItem[]> {
  const {
    global_realestates,
    global_segments,
    filter_external_clients,
    global_companies,
  } = filter;

  // Create a list of tasks only for categories with non-empty input arrays
  const tasks: FilterTask[] = [];

  if (global_realestates.length > 0) {
    tasks.push({
      contentType: 'objects.realestate',
      ids: global_realestates,
      type: 'global_realestates',
    });
  }

  if (global_segments.length > 0) {
    tasks.push({
      contentType: 'objects.segment',
      ids: global_segments,
      type: 'global_segments',
    });
  }

  if (filter_external_clients.length > 0) {
    tasks.push({
      contentType: 'organization.externalclient',
      ids: filter_external_clients,
      type: 'filter_external_clients',
    });
  }

  if (global_companies.length > 0) {
    tasks.push({
      contentType: 'organization.company',
      ids: global_companies,
      type: 'global_companies',
    });
  }

  // Map each task to a promise calling getGlobalFilterItemsForModel
  const promises = tasks.map((task) =>
    getGlobalFilterItemsForModel(task.contentType, task.ids)
  );

  // Run all promises in parallel and collect results
  const results = await Promise.all(promises);

  // Iterate the resolved results alongside the original task descriptors
  const globalFilter: GlobalFilterItem[] = [];
  for (let i = 0; i < results.length; i++) {
    const task = tasks[i];
    const items = results[i];

    for (const item of items) {
      globalFilter.push({
        id: item.id,
        type: task.type,
        displayString: item.name ?? '',
      });
    }
  }

  return globalFilter;
}

/** Rejects on failure by design: a resolve error must not read as "this filter
 *  is empty" — callers persist what they render, so a swallowed failure would
 *  silently drop the unresolved ids from the saved filter. */
async function getGlobalFilterItemsForModel<T extends FilterModel>(
  contentType: ContentType,
  ids: string[]
): Promise<T[]> {
  const res = await fetchApiQuery(
    query(() =>
      api.generic(contentType).query.ids.queryOptions({ params: { ids } })
    )
  );

  return (res ?? []) as T[];
}

export function computeToggledFilteredItems(
  prevItems: GlobalFilterItem[],
  item: GlobalFilterItem
): { items: GlobalFilterItem[]; shouldToast: boolean } {
  // If item already exists, remove it
  if (prevItems.find((p) => p.id === item.id && p.type === item.type)) {
    return {
      items: prevItems.filter(
        (p) => !(p.id === item.id && p.type === item.type)
      ),
      shouldToast: false,
    };
  }

  // If adding a segment, keep only segments
  if (item.type === 'global_segments') {
    const prevWithSegmentsOnly = prevItems.filter(
      (p) => p.type === 'global_segments'
    );
    return {
      items: [...prevWithSegmentsOnly, item],
      shouldToast: prevWithSegmentsOnly.length < prevItems.length,
    };
  }

  // If adding non-segment but segments exist, clear segments
  if (prevItems.find((p) => p.type === 'global_segments')) {
    const prevWithoutSegments = prevItems.filter(
      (p) => p.type !== 'global_segments'
    );
    return {
      items: [...prevWithoutSegments, item],
      shouldToast: true,
    };
  }

  // Default: just add the item
  return {
    items: [...prevItems, item],
    shouldToast: false,
  };
}
